import { create } from "zustand";
import { authenticatedAxios, baseAxios, baseURL } from "../../apis";

const useBlogStore = create((set, get) => ({
  articles: [],
  articleDetail: null,
  markdown: "",

  pagination: {
    page: 1,
    size: 20,
    max_page: 1,
    total_items: 0,
  },

  loadingList: false,
  loadingAction: false,
  loadingDetail: false,
  loadingMarkdown: false,
  error: null,

  actions: {
    // ================================
    // GET LIST
    // ================================
    fetchList: async ({ page = 1, size = 20, language = "en" }) => {
      set({ loadingList: true, error: null });

      try {
        const { data } = await authenticatedAxios.get(
          `${baseURL}/api/admin/blogs/`,
          {
            params: { page, size, language },
          }
        );

        set({
          articles: data.data || data || [],
          pagination: data.pagination || {
            page,
            size,
            max_page: 1,
            total_items: data?.length ?? 0,
          },
        });
      } catch (e) {
        set({ error: e?.response?.data?.message || e.message });
      } finally {
        set({ loadingList: false });
      }
    },

    // ================================
    // FETCH ARTICLE DETAIL
    // ================================
    fetchDetail: async (slug, language = "en") => {
      set({ loadingDetail: true, error: null });

      try {
        const { data } = await authenticatedAxios.get(
          `${baseURL}/api/admin/blogs/${slug}/`,
          { params: { language } }
        );

        set({ articleDetail: data });
      } catch (e) {
        set({ error: e?.response?.data?.message || e.message });
      } finally {
        set({ loadingDetail: false });
      }
    },

    // ================================
    // FETCH MARKDOWN CONTENT
    // ================================
    fetchMarkdown: async (slug, language = "en") => {
      set({ loadingMarkdown: true });

      try {
        const res = await authenticatedAxios.get(
          `/api/admin/blogs/${slug}/markdown`,
          {
            params: { language },
            responseType: "text",
          }
        );

        set({ markdown: res.data });
      } catch (e) {
        set({ markdown: "Failed to load markdown." });
      } finally {
        set({ loadingMarkdown: false });
      }
    },

    // ================================
    // UPDATE BLOG (FormData)
    // ================================
    updateBlog: async (slug, formData, language = "en") => {
      set({ loadingAction: true });

      try {
        await authenticatedAxios.put(
          `${baseURL}/api/admin/edit-blog/${slug}/`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        return true;
      } catch (e) {
        console.error(e);
        set({ error: e?.response?.data?.message || e.message });
        return false;
      } finally {
        set({ loadingAction: false });
      }
    },
    createBlog: async (formData, language = "en") => {
      set({ loadingAction: true });

      try {
        await authenticatedAxios.post(
          `${baseURL}/api/admin/article-upload`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        return true;
      } catch (e) {
        console.error(e);
        set({ error: e?.response?.data?.message || e.message });
        return false;
      } finally {
        set({ loadingAction: false });
      }
    },

    deleteBlog: async (slug, language = "en") => {
      set({ loadingAction: true, error: null });

      try {
        await authenticatedAxios.delete(
          `${baseURL}/api/admin/delete-blog/${slug}/`,
          {
            params: { language },
          }
        );

        // Remove deleted article from local state
        set({
          articles: get().articles.filter((a) => a.slug !== slug),
        });

        return true;
      } catch (e) {
        console.error(e);
        set({ error: e?.response?.data?.message || e.message });
        return false;
      } finally {
        set({ loadingAction: false });
      }
    },
  },
}));

export default useBlogStore;
